import Dexie, { type Table } from 'dexie';
import { getDatabaseName } from '@/lib/appMode';

/**
 * Sync metadata fields present on all synced tables.
 * These fields enable LWW conflict resolution and soft deletes.
 */
export interface SyncMetadata {
  /** ISO timestamp of last modification (used for LWW conflict resolution) */
  syncUpdatedAt: string;
  /** ISO timestamp if soft-deleted, undefined if active */
  syncDeletedAt?: string;
  /** Device ID that made the last change */
  syncDeviceId?: string;
  /** Monotonic version incremented on each save (used for optimistic locking) */
  syncVersion: number;
}

/**
 * Sync outbox entry for queuing mutations when offline.
 */
export interface SyncOutboxRow {
  id: string;
  /** Idempotency key for safe retries */
  idempotencyKey: string;
  /** Target table name */
  table: string;
  /** ID of the record being synced */
  recordId: string;
  /** Operation type */
  operation: 'upsert' | 'delete';
  /** Full record payload for upsert, or minimal info for delete */
  payload: unknown;
  /** ISO timestamp when queued */
  createdAt: string;
  /** Number of retry attempts */
  retryCount: number;
  /** Last error message if failed */
  lastError?: string;
}

export interface ReportRow extends SyncMetadata {
  id: string;
  version: number;
  /** JSON string containing the full report data (simplified from Blob) */
  payload: string;
  companyId: string;
  siteId: string;
  lockedBy?: string;
  lockedAt?: string;
  lockedDevice?: string;
}

/**
 * KV store is device-local (sync cursors, transient state) - no sync metadata.
 */
export interface KvRow {
  key: string;
  value: unknown;
}

/**
 * Meta KV store for working set state, watermarks, and draft metadata.
 */
export interface MetaRow {
  key: string;
  value: unknown;
}

/**
 * Media blob storage for offline attachments.
 */
export interface MediaRow {
  id: string;
  reportId: string;
  storagePath?: string;
  blob: Blob | null;
  mimeType?: string;
  byteSize?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyRow extends SyncMetadata {
  id: string;
  name: string;
  code?: string;
  vat?: string;
  sedeLegale?: string;
  sedeLegaleAddress?: string;
  sedeLegaleCap?: string;
  sedeLegaleCity?: string;
  sedeLegaleProvince?: string;
  notes?: string;
  logoUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ContactRow extends SyncMetadata {
  id: string;
  companyId: string;
  siteId?: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  role?: string;
  scope?: 'company' | 'site';
  reportRecipient?: boolean;
  alarmContact?: boolean;
  fireTraining?: boolean;
  firstAidTraining?: boolean;
  licensed?: boolean;
  licenseExpiry?: string;
  licenseLastUpdate?: string;
  reperibile?: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface SiteRow extends SyncMetadata {
  id: string;
  companyId: string;
  name: string;
  sequenceNumber: number;
  contactEmail?: string;
  contactPhone?: string;
  btGreaseBandPresent?: boolean;
  address?: string;
  siteLocation?: string;
  siteType?: string;
  frequency?: string;
  gasType?: string;
  toxicGas?: string;
  cpiPresent?: boolean;
  cpiProtocolNumber?: string;
  cpiLastRenewalDate?: string;
  cpiExpiryDate?: string;
  ammoniaActivityCodes?: boolean;
  authorizationProtocolNumber?: string;
  authorizationIssueDate?: string;
  authorizationExpiryDate?: string;
  authorizedNh3Kg?: number;
  officialGazettePublished?: boolean;
  officialGazetteDate?: string;
  authorizationPrescriptions?: string;
  periodicityControlPeriod?: string;
  periodicityPreviousVisit?: string;
  periodicityNextVisit?: string;
  periodicityNextVisitAt?: string;
  maintenanceNotes?: string;
  sedeOperativa?: string;
  sedeOperativaAddress?: string;
  sedeOperativaCap?: string;
  sedeOperativaCity?: string;
  sedeOperativaProvince?: string;
  periodicity?: '1' | '3' | '4' | '6'; // months between inspections
  contractActivationDate?: string; // ISO date string (YYYY-MM-01 from month/year input)
  // Computed fields (derived from activation date + periodicity)
  nextInspectionWindowStart?: string;
  nextInspectionWindowEnd?: string;
  // Keep essential fields for existing functionality
  refrigerationUnitsCount?: number;
  siteComponents?: Array<{
    id?: string;
    type: string;
    quantity: number;
    notes?: string;
  }>;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UnitRow extends SyncMetadata, RefrigerationUnitRow {
  siteId: string;
  companyId: string;
}

export interface RefrigerationUnitComponentRow {
  id: string;
  type?: string;
  customLabel?: string;
  quantity?: number;
  capacityKw?: number;
  location?: string;
  notes?: string;
  btGreaseBand?: boolean;
}

export interface RefrigerationUnitSafetyThresholdRow {
  id: string;
  thresholdType: string;
  unit: string;
  value: number;
  actions: string[];
}

interface RefrigerationUnitSafetyRow {
  detectors: {
    toxicCount?: number;
    explosiveCount?: number;
    positions?: string[];
    thresholds: RefrigerationUnitSafetyThresholdRow[];
    calibrationNotes?: string;
    ceilingCount?: number;
    humanHeightCount?: number;
  };
  ventilation?: {
    type?: string;
    flowRateM3h?: number;
    notes?: string;
    naturalPresent?: boolean;
    apertureHighM2?: number;
    apertureLowM2?: number;
    natural?: {
      highOpeningsAreaM2?: number;
      lowOpeningsAreaM2?: number;
      notes?: string;
    };
    extractors?: {
      units?: number;
      airflowM3h?: number;
      atex?: boolean;
      logic?: string;
      mode?: string;
    };
    extractorsCount?: number;
    extractorsFlowM3h?: number;
    extractorsOperation?: string;
    extractorsAtex?: boolean;
  };
  scrubber?: {
    present: boolean;
    notes?: string;
    airFlowM3h?: number;
    waterFlowM3h?: number;
    waterFlowSpec?: string;
    contactTimeSeconds?: number;
    waterSupply?: string;
    wasteCollection?: string;
    atex?: boolean;
  };
  abatementSystem?: {
    present: boolean;
    controlMode?: 'manuale' | 'automatico';
    trim?: boolean;
    limitSwitches?: boolean;
    type?: string;
    supply?: string;
    disposal?: string;
    waterFlowM3h?: number;
  };
  powerBackup?: {
    preferredLine?: boolean;
    generator?: boolean;
    ups?: boolean;
    notes?: string;
  };
  emergencySystems?: {
    dialerPresent?: boolean;
    guardServicePresent?: boolean;
    generatorPresent?: boolean;
    notes?: string;
    eeTripButtons?: boolean;
    eeTripDetector?: boolean;
    elettroserraturaPresent?: boolean;
    emergencyLightingPresent?: boolean;
    emergencyLightingAtex?: boolean;
    sgancioAutomaticoPresent?: boolean;
    sogliaSgancioAutomatico?: string;
    pulsanteSgancioPresent?: boolean;
  };
  detectionMaintenance?: {
    name?: string;
    notes?: string;
  };
  valves?: {
    reliefSetPressuresBar?: number[];
    reliefPeriodicityYears?: number;
    reliefNotes?: string;
    isolationPresent?: boolean;
    isolationDiverter?: boolean;
    isolationNotes?: string;
  };
  vsRelief?: {
    collectorsCount?: number;
    sizing?: string;
  };
  abatementTank?: {
    present?: boolean;
    volumeMc?: number;
    minWaterMc?: number;
    minFreeMc?: number;
    location?: string;
    signage?: string;
    accessibility?: string;
    material?: string;
    dimensions?: string;
    orientation?: string;
    levelInstrumentation?: {
      present?: boolean;
      type?: string;
    };
    continuousPhMonitoring?: boolean;
    emergencySupply?: {
      preferredLine?: boolean;
      generator?: boolean;
      ups?: boolean;
    };
  };
  vvfDocs?: string;
  vvfDocsLocation?: string;
}

export interface RefrigerationUnitTechnicalRow {
  components: RefrigerationUnitComponentRow[];
  maintenanceContractor?: {
    name?: string;
    notes?: string;
  };
  safety: RefrigerationUnitSafetyRow;
  notes?: string;
}

export interface RefrigerationUnitRow {
  id: string;
  refrigerationUnitId: string;
  name: string;
  location?: unknown;
  reperibilePhone?: string;
  ammoniaKg?: number;
  toxicGasType?: unknown;
  impiantoType?: unknown;
  plannedPeriod?: string;
  plannedDate?: string;
  plannedSource?: string;
  plannedUpdatedAt?: string;
  technical: RefrigerationUnitTechnicalRow;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export type ActivityPriority = 'low' | 'medium' | 'high';

/** @deprecated Use ActivityPriority instead */
export type ActionPriority = ActivityPriority;

/**
 * Activity status for workflow tracking.
 */
export type ActivityStatus = 'pending' | 'in_progress' | 'completed';

/**
 * Activity category for unified activity system.
 * All trackable work items are stored as activities.
 */
export type ActivityCategory =
  | 'manual' // User-created activities
  | 'nc' // Non-conformity from report
  | 'training' // Training scheduled from report
  | 'certification' // License expiry from personnel
  | 'calibration' // Sensor calibration from report
  | 'draft-report' // Complete draft report
  | 'schedule-inspection' // Schedule site inspection
  | 'inspection-reminder' // Reminder before scheduled visit
  | 'cpi-renewal' // CPI certificate renewal
  | 'authorization' // Authorization renewal
  | 'gu-publication'; // Official gazette publication

/** @deprecated Use ActivityCategory instead */
export type ActionCategory = ActivityCategory;

/**
 * Source type for activity deduplication.
 */
export type ActivitySourceType = 'report' | 'personnel' | 'site' | 'schedule' | 'manual';

/** @deprecated Use ActivitySourceType instead */
export type ActionSourceType = ActivitySourceType;

/**
 * Reference to any entity related to an activity.
 * Used for flexible cross-entity linking beyond indexed fields.
 */
export interface EntityRef {
  type: 'company' | 'site' | 'report' | 'person' | 'unit';
  id: string;
  /** Cached display label for UI rendering */
  label?: string;
}

export interface ActivityRow extends SyncMetadata {
  id: string;
  companyId: string;
  siteId?: string;
  reportId?: string;
  reportTitle?: string;
  reportVisitDate?: string;
  reportSiteName?: string;
  title: string;
  type: 'system' | 'internal' | 'external';
  /** Category for unified activity system */
  category?: ActivityCategory;
  priority?: ActivityPriority;
  status: ActivityStatus;
  dueDate?: string;
  /** Source type for deduplication (e.g., "report", "personnel") */
  sourceType?: ActivitySourceType;
  /** Source ID for deduplication (e.g., "nc-{reportId}-{ncId}") */
  sourceId?: string;
  /** Additional description for the activity */
  description?: string;
  /** Person ID for certification activities */
  personId?: string;

  // Workflow timestamps
  /** When work started (status → in_progress) */
  startedAt?: string;
  /** When work completed (status → completed) */
  completedAt?: string;

  // Time-based trigger fields
  /** When this activity was auto-triggered */
  triggerDate?: string;
  /** For reminder-type activities: days before target event */
  reminderDaysBefore?: number;

  // Flexible entity references (supplements indexed fields)
  references?: EntityRef[];

  createdAt: string;
  updatedAt: string;
}

/** @deprecated Use ActivityRow instead */
export type ActionRow = ActivityRow;

export interface ComponentCategoryRow extends SyncMetadata {
  id: string;
  label: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SchemaRow extends SyncMetadata {
  id: string;
  version: string;
  definition: unknown; // JSON blob containing ReportSchemaDefinition
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateRow extends SyncMetadata {
  _key: string; // Composite key: "${id}@${version}"
  id: string;
  version: string;
  name: string;
  description?: string;
  sections: unknown[]; // TemplateSection[]
  metadata: {
    targetType: 'ammonia-audit';
    createdAt: string;
    updatedAt: string;
  };
}

export class AppDB extends Dexie {
  reports!: Table<ReportRow, string>;
  companies!: Table<CompanyRow, string>;
  sites!: Table<SiteRow, string>;
  contacts!: Table<ContactRow, string>;
  units!: Table<UnitRow, string>;
  activities!: Table<ActivityRow, string>;
  schemas!: Table<SchemaRow, string>;
  templates!: Table<TemplateRow, string>;
  kv!: Table<KvRow, string>;
  meta!: Table<MetaRow, string>;
  media!: Table<MediaRow, string>;
  componentCategories!: Table<ComponentCategoryRow, string>;
  syncOutbox!: Table<SyncOutboxRow, string>;

  /** @deprecated Use activities instead */
  get actions(): Table<ActivityRow, string> {
    return this.activities;
  }

  constructor(databaseName = 'ammonia_audit_db') {
    super(databaseName);

    // Version 17: Baseline schema (dev data is reset via Settings/SeedData).
    // Legacy migrations were removed to keep the schema lean.
    this.version(17).stores({
      reports: 'id, version, companyId, siteId',
      companies: 'id, name, createdAt',
      sites: 'id, companyId, name, createdAt',
      alerts: 'id, companyId, siteId, reportId, status, dueDate',
      schemas: 'id, version, isDefault, createdAt',
      templates: '_key, id, version, metadata.createdAt, metadata.updatedAt',
      kv: 'key',
      componentCategories: 'id, label, createdAt',
      actionItems: null,
      clients: null,
    });

    // Version 18: Add sync metadata indexes and sync outbox table.
    // - syncUpdatedAt index on synced tables for delta queries
    // - syncOutbox table for queuing offline mutations
    this.version(18).stores({
      reports: 'id, version, companyId, siteId, syncUpdatedAt',
      companies: 'id, name, createdAt, syncUpdatedAt',
      sites: 'id, companyId, name, createdAt, syncUpdatedAt',
      alerts: 'id, companyId, siteId, reportId, status, dueDate, syncUpdatedAt',
      schemas: 'id, version, isDefault, createdAt, syncUpdatedAt',
      templates: '_key, id, version, metadata.createdAt, metadata.updatedAt, syncUpdatedAt',
      kv: 'key',
      componentCategories: 'id, label, createdAt, syncUpdatedAt',
      syncOutbox: 'id, table, recordId, createdAt',
    });

    // Version 19: Add composite index for outbox deduplication.
    this.version(19).stores({
      reports: 'id, version, companyId, siteId, syncUpdatedAt',
      companies: 'id, name, createdAt, syncUpdatedAt',
      sites: 'id, companyId, name, createdAt, syncUpdatedAt',
      alerts: 'id, companyId, siteId, reportId, status, dueDate, syncUpdatedAt',
      schemas: 'id, version, isDefault, createdAt, syncUpdatedAt',
      templates: '_key, id, version, metadata.createdAt, metadata.updatedAt, syncUpdatedAt',
      kv: 'key',
      componentCategories: 'id, label, createdAt, syncUpdatedAt',
      syncOutbox: 'id, table, recordId, createdAt, [table+recordId]',
    });

    // Version 20: Add company code/vat indexes for uniqueness checks.
    this.version(20).stores({
      reports: 'id, version, companyId, siteId, syncUpdatedAt',
      companies: 'id, name, code, vat, createdAt, syncUpdatedAt',
      sites: 'id, companyId, name, createdAt, syncUpdatedAt',
      alerts: 'id, companyId, siteId, reportId, status, dueDate, syncUpdatedAt',
      schemas: 'id, version, isDefault, createdAt, syncUpdatedAt',
      templates: '_key, id, version, metadata.createdAt, metadata.updatedAt, syncUpdatedAt',
      kv: 'key',
      componentCategories: 'id, label, createdAt, syncUpdatedAt',
      syncOutbox: 'id, table, recordId, createdAt, [table+recordId]',
    });

    // Version 21: Add media + meta tables and idempotency keys for the outbox.
    this.version(21).stores({
      reports: 'id, version, companyId, siteId, syncUpdatedAt',
      companies: 'id, name, code, vat, createdAt, syncUpdatedAt',
      sites: 'id, companyId, name, createdAt, syncUpdatedAt',
      alerts: 'id, companyId, siteId, reportId, status, dueDate, syncUpdatedAt',
      schemas: 'id, version, isDefault, createdAt, syncUpdatedAt',
      templates: '_key, id, version, metadata.createdAt, metadata.updatedAt, syncUpdatedAt',
      kv: 'key',
      meta: 'key',
      media: 'id, reportId, storagePath, createdAt',
      componentCategories: 'id, label, createdAt, syncUpdatedAt',
      syncOutbox: 'id, idempotencyKey, table, recordId, createdAt, [table+recordId]',
    });

    // Version 22: Add units + contacts tables and migrate nested data.
    this.version(22)
      .stores({
        reports: 'id, version, companyId, siteId, syncUpdatedAt',
        companies: 'id, name, code, vat, createdAt, syncUpdatedAt',
        sites: 'id, companyId, name, createdAt, syncUpdatedAt',
        contacts: 'id, companyId, siteId, syncUpdatedAt',
        units: 'id, companyId, siteId, syncUpdatedAt',
        alerts: 'id, companyId, siteId, reportId, status, dueDate, syncUpdatedAt',
        schemas: 'id, version, isDefault, createdAt, syncUpdatedAt',
        templates: '_key, id, version, metadata.createdAt, metadata.updatedAt, syncUpdatedAt',
        kv: 'key',
        meta: 'key',
        media: 'id, reportId, storagePath, createdAt',
        componentCategories: 'id, label, createdAt, syncUpdatedAt',
        syncOutbox: 'id, idempotencyKey, table, recordId, createdAt, [table+recordId]',
      })
      .upgrade(async (tx) => {
        const now = new Date().toISOString();
        const companies = await tx.table('companies').toArray();
        const sites = await tx.table('sites').toArray();
        const contactsTable = tx.table('contacts');
        const unitsTable = tx.table('units');

        for (const company of companies) {
          const personnel = Array.isArray((company as { personnel?: unknown }).personnel)
            ? ((company as { personnel?: unknown }).personnel as Array<Record<string, unknown>>)
            : [];
          for (const person of personnel) {
            const entry = person as Record<string, unknown>;
            await contactsTable.add({
              id: String(entry.id ?? ''),
              companyId: company.id,
              siteId: typeof entry.siteId === 'string' ? entry.siteId : undefined,
              firstName: String(entry.firstName ?? ''),
              lastName: String(entry.lastName ?? ''),
              email: typeof entry.email === 'string' ? entry.email : undefined,
              phone: typeof entry.phone === 'string' ? entry.phone : undefined,
              role: typeof entry.role === 'string' ? entry.role : undefined,
              scope: entry.scope === 'site' || entry.scope === 'company' ? entry.scope : undefined,
              reportRecipient: Boolean(entry.reportRecipient),
              alarmContact: Boolean(entry.alarmContact),
              fireTraining: Boolean(entry.fireTraining),
              firstAidTraining: Boolean(entry.firstAidTraining),
              licensed: Boolean(entry.licensed),
              licenseExpiry:
                typeof entry.licenseExpiry === 'string' ? entry.licenseExpiry : undefined,
              licenseLastUpdate:
                typeof entry.licenseLastUpdate === 'string' ? entry.licenseLastUpdate : undefined,
              reperibile: Boolean(entry.reperibile),
              createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : now,
              updatedAt: typeof entry.updatedAt === 'string' ? entry.updatedAt : now,
              deletedAt: typeof entry.deletedAt === 'string' ? entry.deletedAt : undefined,
              syncUpdatedAt: now,
              syncDeletedAt: typeof entry.deletedAt === 'string' ? entry.deletedAt : undefined,
              syncDeviceId: company.syncDeviceId,
              syncVersion: company.syncVersion ?? 0,
            } satisfies ContactRow);
          }
        }

        for (const site of sites) {
          const units = Array.isArray((site as { refrigerationUnits?: unknown }).refrigerationUnits)
            ? ((site as { refrigerationUnits?: unknown }).refrigerationUnits as Array<
                Record<string, unknown>
              >)
            : [];
          let activeCount = 0;
          for (const unit of units) {
            const entry = unit as Record<string, unknown>;
            if (!entry.deletedAt) {
              activeCount += 1;
            }
            await unitsTable.add({
              ...(entry as Record<string, unknown>),
              siteId: site.id,
              companyId: site.companyId,
              syncUpdatedAt: now,
              syncDeletedAt: typeof entry.deletedAt === 'string' ? entry.deletedAt : undefined,
              syncDeviceId: site.syncDeviceId,
              syncVersion: site.syncVersion ?? 0,
            } as UnitRow);
          }

          await tx.table('sites').update(site.id, {
            refrigerationUnitsCount: activeCount,
          });
        }

        await tx
          .table('companies')
          .toCollection()
          .modify((company) => {
            delete company.personnel;
          });

        await tx
          .table('sites')
          .toCollection()
          .modify((site) => {
            delete site.refrigerationUnits;
          });
      });

    // Version 23: Add structured address fields for companies and sites.
    this.version(23).stores({
      reports: 'id, version, companyId, siteId, syncUpdatedAt',
      companies: 'id, name, code, vat, createdAt, syncUpdatedAt',
      sites: 'id, companyId, name, createdAt, syncUpdatedAt',
      contacts: 'id, companyId, siteId, syncUpdatedAt',
      units: 'id, companyId, siteId, syncUpdatedAt',
      alerts: 'id, companyId, siteId, reportId, status, dueDate, syncUpdatedAt',
      schemas: 'id, version, isDefault, createdAt, syncUpdatedAt',
      templates: '_key, id, version, metadata.createdAt, metadata.updatedAt, syncUpdatedAt',
      kv: 'key',
      meta: 'key',
      media: 'id, reportId, storagePath, createdAt',
      componentCategories: 'id, label, createdAt, syncUpdatedAt',
      syncOutbox: 'id, idempotencyKey, table, recordId, createdAt, [table+recordId]',
    });

    // Version 24: Rename alerts table to actions.
    this.version(24).stores({
      reports: 'id, version, companyId, siteId, syncUpdatedAt',
      companies: 'id, name, code, vat, createdAt, syncUpdatedAt',
      sites: 'id, companyId, name, createdAt, syncUpdatedAt',
      contacts: 'id, companyId, siteId, syncUpdatedAt',
      units: 'id, companyId, siteId, syncUpdatedAt',
      actions: 'id, companyId, siteId, reportId, status, dueDate, syncUpdatedAt',
      alerts: null,
      schemas: 'id, version, isDefault, createdAt, syncUpdatedAt',
      templates: '_key, id, version, metadata.createdAt, metadata.updatedAt, syncUpdatedAt',
      kv: 'key',
      meta: 'key',
      media: 'id, reportId, storagePath, createdAt',
      componentCategories: 'id, label, createdAt, syncUpdatedAt',
      syncOutbox: 'id, idempotencyKey, table, recordId, createdAt, [table+recordId]',
    });

    // Version 25: Migrate Blob payloads to JSON strings in reports table.
    // This handles legacy data from before the serialization simplification.
    this.version(25)
      .stores({
        reports: 'id, version, companyId, siteId, syncUpdatedAt',
        companies: 'id, name, code, vat, createdAt, syncUpdatedAt',
        sites: 'id, companyId, name, createdAt, syncUpdatedAt',
        contacts: 'id, companyId, siteId, syncUpdatedAt',
        units: 'id, companyId, siteId, syncUpdatedAt',
        actions: 'id, companyId, siteId, reportId, status, dueDate, syncUpdatedAt',
        schemas: 'id, version, isDefault, createdAt, syncUpdatedAt',
        templates: '_key, id, version, metadata.createdAt, metadata.updatedAt, syncUpdatedAt',
        kv: 'key',
        meta: 'key',
        media: 'id, reportId, storagePath, createdAt',
        componentCategories: 'id, label, createdAt, syncUpdatedAt',
        syncOutbox: 'id, idempotencyKey, table, recordId, createdAt, [table+recordId]',
      })
      .upgrade(async (tx) => {
        const reportsTable = tx.table('reports');
        const reports = await reportsTable.toArray();

        for (const report of reports) {
          const row = report as Record<string, unknown>;
          const payload = row.payload;

          // Check if payload is a Blob (legacy format)
          if (payload instanceof Blob) {
            try {
              const text = await payload.text();
              await reportsTable.update(row.id as string, { payload: text });
              console.log(
                `[DB Migration v25] Converted Blob payload to string for report ${row.id}`
              );
            } catch (error) {
              console.error(
                `[DB Migration v25] Failed to convert payload for report ${row.id}:`,
                error
              );
            }
          }
        }
      });

    // Version 26: Add category and source tracking fields to actions table.
    // This supports the unified action system where all actionable items
    // (NC, training, certifications, etc.) are stored as actions.
    this.version(26).stores({
      reports: 'id, version, companyId, siteId, syncUpdatedAt',
      companies: 'id, name, code, vat, createdAt, syncUpdatedAt',
      sites: 'id, companyId, name, createdAt, syncUpdatedAt',
      contacts: 'id, companyId, siteId, syncUpdatedAt',
      units: 'id, companyId, siteId, syncUpdatedAt',
      actions:
        'id, companyId, siteId, reportId, status, dueDate, category, sourceType, sourceId, syncUpdatedAt',
      schemas: 'id, version, isDefault, createdAt, syncUpdatedAt',
      templates: '_key, id, version, metadata.createdAt, metadata.updatedAt, syncUpdatedAt',
      kv: 'key',
      meta: 'key',
      media: 'id, reportId, storagePath, createdAt',
      componentCategories: 'id, label, createdAt, syncUpdatedAt',
      syncOutbox: 'id, idempotencyKey, table, recordId, createdAt, [table+recordId]',
    });

    // Version 27: Rename actions → activities table and status "open" → "pending".
    // This supports the unified activity system for all trackable work items.
    this.version(27)
      .stores({
        reports: 'id, version, companyId, siteId, syncUpdatedAt',
        companies: 'id, name, code, vat, createdAt, syncUpdatedAt',
        sites: 'id, companyId, name, createdAt, syncUpdatedAt',
        contacts: 'id, companyId, siteId, syncUpdatedAt',
        units: 'id, companyId, siteId, syncUpdatedAt',
        activities:
          'id, companyId, siteId, reportId, status, dueDate, category, sourceType, sourceId, syncUpdatedAt',
        actions: null, // Remove old table
        schemas: 'id, version, isDefault, createdAt, syncUpdatedAt',
        templates: '_key, id, version, metadata.createdAt, metadata.updatedAt, syncUpdatedAt',
        kv: 'key',
        meta: 'key',
        media: 'id, reportId, storagePath, createdAt',
        componentCategories: 'id, label, createdAt, syncUpdatedAt',
        syncOutbox: 'id, idempotencyKey, table, recordId, createdAt, [table+recordId]',
      })
      .upgrade(async (tx) => {
        // Migrate data from actions to activities with status rename
        const oldTable = tx.table('actions');
        const newTable = tx.table('activities');

        const actions = await oldTable.toArray();
        for (const action of actions) {
          const activity = {
            ...action,
            // Rename status: "open" → "pending"
            status: action.status === 'open' ? 'pending' : action.status,
          };
          await newTable.add(activity);
        }

        console.log(`[DB Migration v27] Migrated ${actions.length} actions to activities`);
      });
  }
}

/**
 * Lazy singleton for the database instance.
 * Database name is determined by the current app mode (demo/real).
 */
let _db: AppDB | null = null;

/**
 * Get the database instance. Creates it lazily on first access.
 * Uses mode-aware database name from appMode store.
 */
export function getDb(): AppDB {
  if (!_db) {
    const dbName = getDatabaseName();
    _db = new AppDB(dbName);
  }
  return _db;
}

/**
 * Reset the database instance. Call this before switching app modes.
 * The next call to getDb() will create a new instance with the new mode's database name.
 */
export async function resetDbInstance(): Promise<void> {
  if (_db) {
    _db.close();
    _db = null;
  }
}

/**
 * @deprecated Use getDb() instead. This singleton is kept temporarily for backwards compatibility.
 * It will be removed once all imports are migrated to getDb().
 */
export const db = getDb();
